import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { userSchema } from '@/validation-schemas'
import { CaseWorkerRole } from '@repo/shared/dtos/caseWorker'
import { Button } from '@repo/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@repo/ui/card'
import { DialogFooter } from '@repo/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { z } from '@repo/ui/lang'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/select'
import { Separator } from '@repo/ui/separator'
import { useMemo } from 'react'

export function Invite() {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const defaultValues = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: '',
      role: CaseWorkerRole.CaseWorker,
    }),
    [],
  )

  const { form, submit, isFormValid, submitOnPressMetaEnter } =
    useFormValidator({
      defaultValues,
      onSubmit: (values) => {
        void dispatch(adminStoreActions.caseWorker.invite(values))
        form.reset()
      },
      schema: {
        firstName: userSchema.firstName,
        lastName: userSchema.lastName,
        email: userSchema.email,
        role: z.nativeEnum(CaseWorkerRole),
      },
    })

  const roles = Object.values(CaseWorkerRole)
  return (
    <Card className="max-h-fit">
      <CardHeader>
        <CardDescription>Invite new members by email address</CardDescription>
      </CardHeader>
      <CardContent>
        <Separator className="mb-4" />
        <Form {...form}>
          <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter}>
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{tc('firstName')}</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder={tc('firstName')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{tc('lastName')}</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder={tc('lastName')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{tc('email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={tc('email')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="flex-shrink-0">
                      <FormLabel>{tc('role', { cfl: true })}</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={defaultValues.role}
                        >
                          <SelectTrigger className="max-w-fit">
                            <SelectValue
                              placeholder={tc('role', { cfl: true })}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map((v, i) => (
                              <SelectItem value={v} key={i} className="pl-2">
                                {v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button type="submit" size="sm" disabled={!isFormValid}>
                {tc('invite', { cfl: true })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
